/**
 * Componente reutilizable de selección múltiple.
 * Estilo Cyber-Minimalist. API genérica: items, selected, displayField, trackBy.
 *
 * @author: Marcos Cordo
 * @version: 15/05/2026_1a-fix (Eliminada purga automática en ngOnChanges)
 *
 * HISTORIA: La versión anterior purgaba 'selected' cuando alguno de sus items
 * no estaba en 'items'. La intención era de "red de seguridad", pero generaba
 * un bug de carga inicial: durante la inicialización del padre, [items] puede
 * llegar vacío momentáneamente (porque el catálogo aún se está filtrando) y
 * [selected] ya tiene datos. La purga eliminaba los datos válidos del usuario.
 *
 * Decisión: la depuración semántica de seleccionados (ej. cambio de cliente)
 * es responsabilidad EXCLUSIVA del padre, que sabe distinguir un catálogo
 * "en transición" de un catálogo "que cambió por decisión del usuario".
 */
import { Component, Input, Output, EventEmitter, ElementRef, HostListener } from '@angular/core';

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss']
})
export class MultiSelectComponent {
  @Input() items: any[] = [];
  @Input() selected: any[] = [];
  @Input() displayField: string = 'name';
  @Input() trackBy: string = 'id';
  @Input() placeholder: string = 'Seleccionar...';
  @Input() disabled: boolean = false;
  @Input() searchable: boolean = true;

  @Output() selectionChange = new EventEmitter<any[]>();

  isOpen: boolean = false;
  filter: string = '';

  constructor(private elementRef: ElementRef) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (!this.isOpen) this.filter = '';
  }

  isSelected(item: any): boolean {
    const key = this.getKey(item);
    return this.selected.some(s => this.getKey(s) === key);
  }

  toggleItem(item: any, event: MouseEvent): void {
    event.stopPropagation();
    if (this.disabled) return;

    const key = this.getKey(item);
    const idx = this.selected.findIndex(s => this.getKey(s) === key);
    const next = [...this.selected];

    if (idx >= 0) {
      next.splice(idx, 1);
    } else {
      next.push(item);
    }
    this.selected = next;
    this.selectionChange.emit(next);
  }

  removeItem(item: any, event: MouseEvent): void {
    event.stopPropagation();
    if (this.disabled) return;
    const key = this.getKey(item);
    const next = this.selected.filter(s => this.getKey(s) !== key);
    this.selected = next;
    this.selectionChange.emit(next);
  }

  get filteredItems(): any[] {
    if (!this.filter) return this.items || [];
    const f = this.filter.toLowerCase();
    return (this.items || []).filter(i => String(i[this.displayField] || '').toLowerCase().includes(f));
  }

  get showSearch(): boolean {
    return this.searchable && Array.isArray(this.items) && this.items.length > 1;
  }

  getDisplay(item: any): string {
    return item ? String(item[this.displayField] ?? '') : '';
  }

  private getKey(item: any): any {
    return item ? item[this.trackBy] : null;
  }

  trackByItem = (_: number, item: any) => this.getKey(item);
}